import React, { useState } from "react";
import { Send, Loader2, CheckCircle2, MapPin, Phone, Mail, Clock, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT } from "@/constants/testIds";
import { toast } from "sonner";

const services = [
  "Auto Detailing - Standard",
  "Auto Detailing - Executive",
  "Auto Detailing - Elite",
  "Auto Detailing - Presidential",
  "House Washing",
  "Driveway Cleaning",
  "Sidewalk Cleaning",
  "Patio Cleaning",
  "Fence Cleaning",
  "Deck Cleaning",
  "Gutter Brightening",
  "Other",
];

export default function ContactForm({ settings }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service_type: "",
    vehicle_or_property: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: typeof e === "string" ? e : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.phone || !form.service_type) {
      setError("Please fill in name, phone and service.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await api.post("/quotes", payload);
      setSuccess(true);
      toast.success("Quote request sent! We'll be in touch shortly.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service_type: "",
        vehicle_or_property: "",
        message: "",
      });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const phoneClean = (settings?.phone || "+1 (619) 769-2398").replace(/[^+\d]/g, "");
  const smsClean = (settings?.sms_phone || "+1 (619) 712-3970").replace(/[^+\d]/g, "");

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Info side */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-cyan-400" />
              <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
                Get a Quote
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-none text-white">
              Let&apos;s make it <span className="text-cyan-400">shine.</span>
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed">
              Tell us about your vehicle or property. We&apos;ll get back to you
              same-day with a personalized quote.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`tel:${phoneClean}`}
                className="flex items-start gap-4 group"
              >
                <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-cyan-400/60 transition-colors">
                  <Phone className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Call us</div>
                  <div className="text-white font-medium mt-1">{settings?.phone || "+1 (619) 769-2398"}</div>
                </div>
              </a>

              {settings?.sms_phone && (
                <a href={`sms:${smsClean}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-cyan-400/60 transition-colors">
                    <MessageSquare className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Text only</div>
                    <div className="text-white font-medium mt-1">{settings.sms_phone}</div>
                  </div>
                </a>
              )}

              {settings?.email && (
                <a href={`mailto:${settings.email}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 border border-white/10 flex items-center justify-center group-hover:border-cyan-400/60 transition-colors">
                    <Mail className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-zinc-500">Email</div>
                    <div className="text-white font-medium mt-1">{settings.email}</div>
                  </div>
                </a>
              )}

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Service area</div>
                  <div className="text-white font-medium mt-1">{settings?.address || "1705 Hwy 59, Mandeville, LA"}</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 border border-white/10 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-cyan-400" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-zinc-500">Hours</div>
                  <div className="text-white font-medium mt-1 whitespace-pre-line">
                    {settings?.hours || "Mon - Fri: 9:00 AM - 6:00 PM\nSat: 9:00 AM - 4:00 PM"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              onSubmit={submit}
              data-testid={CONTACT.form}
              className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="c-name" className="uppercase tracking-widest text-xs text-zinc-400">
                    Full Name *
                  </Label>
                  <Input
                    id="c-name"
                    data-testid={CONTACT.name}
                    value={form.name}
                    onChange={onChange("name")}
                    className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-phone" className="uppercase tracking-widest text-xs text-zinc-400">
                    Phone *
                  </Label>
                  <Input
                    id="c-phone"
                    data-testid={CONTACT.phone}
                    value={form.phone}
                    onChange={onChange("phone")}
                    type="tel"
                    className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
                    placeholder="+1 (619) 555-0000"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="c-email" className="uppercase tracking-widest text-xs text-zinc-400">
                    Email (optional)
                  </Label>
                  <Input
                    id="c-email"
                    data-testid={CONTACT.email}
                    value={form.email}
                    onChange={onChange("email")}
                    type="email"
                    className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
                    placeholder="you@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="uppercase tracking-widest text-xs text-zinc-400">
                    Service *
                  </Label>
                  <Select value={form.service_type} onValueChange={onChange("service_type")}>
                    <SelectTrigger
                      data-testid={CONTACT.serviceType}
                      className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
                    >
                      <SelectValue placeholder="Choose a service..." />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/10">
                      {services.map((s) => (
                        <SelectItem
                          key={s}
                          value={s}
                          className="text-white focus:bg-cyan-400/10 focus:text-cyan-400"
                        >
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="c-vehicle" className="uppercase tracking-widest text-xs text-zinc-400">
                  Vehicle / Property details
                </Label>
                <Input
                  id="c-vehicle"
                  data-testid={CONTACT.vehicle}
                  value={form.vehicle_or_property}
                  onChange={onChange("vehicle_or_property")}
                  className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
                  placeholder="2022 Tesla Model Y / 2-story house"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="c-message" className="uppercase tracking-widest text-xs text-zinc-400">
                  Message
                </Label>
                <Textarea
                  id="c-message"
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={onChange("message")}
                  rows={4}
                  className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white"
                  placeholder="Tell us anything else that helps us quote your job..."
                />
              </div>

              {error && (
                <div
                  data-testid={CONTACT.error}
                  className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-3"
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  data-testid={CONTACT.success}
                  className="flex items-center gap-3 text-sm text-cyan-300 border border-cyan-400/30 bg-cyan-400/5 px-4 py-3"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Quote request sent. We&apos;ll be in touch shortly.
                </div>
              )}

              <Button
                type="submit"
                data-testid={CONTACT.submit}
                disabled={loading}
                className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-sm h-14 px-8 w-full md:w-auto shadow-[0_0_20px_rgba(0,229,255,0.3)]"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 w-4 h-4" />
                    Request my quote
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
